import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { usePhoneInput, CountrySelector } from 'react-international-phone'
import 'react-international-phone/style.css'
import {
  StepHeader,
  OnboardingButton,
  OnboardingSkipButton,
  OnboardingLabel,
  OnboardingInput,
  OnboardingAlert,
} from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useSendLeadPhoneCode, useVerifyLeadPhoneCode } from '@/hooks/api/useLeadOnboarding'
import { useTurnstile } from '@/hooks/useTurnstile'
import { ArrowRightIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

/**
 * Strip all non-digit characters except the leading `+` so the backend
 * receives a strict E.164 phone number (e.g. `+31617413008`).
 */
function toE164(phone: string): string {
  return phone.replace(/(?!^\+)\D/g, '')
}

interface PhoneStepProps {
  onNext: () => void
  onBack: () => void
}

export function PhoneStep({ onNext, onBack }: PhoneStepProps) {
  const { t } = useTranslation()
  const { phoneNumber, setPhoneNumber, setPhoneVerified, leadId } = useOnboardingStore()
  const [showVerification, setShowVerification] = useState(false)
  const [fullPhoneNumber, setFullPhoneNumber] = useState('')
  const [existingPhoneError, setExistingPhoneError] = useState(false)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)

  const phoneInputRef = useRef<HTMLInputElement>(null)

  const sendCode = useSendLeadPhoneCode()
  const verifyCode = useVerifyLeadPhoneCode()

  // Invisible Turnstile gate for send-code (the paid Twilio SMS vector).
  const { getToken: getPhoneTurnstileToken, reset: resetPhoneTurnstile } =
    useTurnstile()

  const {
    phone,
    inputValue,
    country,
    setCountry,
    handlePhoneValueChange,
    inputRef,
  } = usePhoneInput({
    defaultCountry: 'nl',
    value: phoneNumber || undefined,
    inputRef: phoneInputRef,
    onChange: () => {
      setPhoneError(null)
      setExistingPhoneError(false)
    },
  })

  const verifySchema = z.object({
    code: z.string().length(6, t('resources.signup.phone.code_invalid')),
  })
  type VerifyFormData = z.infer<typeof verifySchema>

  const verifyForm = useForm<VerifyFormData>({
    resolver: zodResolver(verifySchema),
  })

  const handleSendCode = async () => {
    // Normalize to E.164 before validation and API call
    const e164Phone = toE164(phone)

    // Validate: phone must have more than just the dial code
    const digitsOnly = e164Phone.replace(/\D/g, '')
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
      setPhoneError(t('resources.signup.phone.error_invalid'))
      return
    }

    setFullPhoneNumber(phone)
    setExistingPhoneError(false)
    setPhoneError(null)

    if (!leadId) return

    setIsSending(true)
    try {
      // Fetch a fresh single-use Turnstile token for each send/resend attempt.
      let turnstileToken: string | undefined
      try {
        turnstileToken = await getPhoneTurnstileToken()
      } catch {
        turnstileToken = undefined
      }

      await sendCode.mutateAsync({ leadId, phoneNumber: e164Phone, turnstileToken })
      setShowVerification(true)
    } catch (error: any) {
      const status = error?.response?.status
      const detail = error?.response?.data?.detail
      const errorMessage = typeof detail === 'string' ? detail : error?.message || ''
      if (errorMessage.toLowerCase().includes('already in use')) {
        setExistingPhoneError(true)
      } else if (status === 422) {
        setPhoneError(t('resources.signup.phone.error_invalid'))
      } else {
        // A 403 means Turnstile verification failed — clear the token so the
        // next attempt requests a fresh one.
        if (status === 403) resetPhoneTurnstile()
        toast.error(t('resources.signup.phone.error_send_failed'), {
          description: t('resources.signup.phone.error_send_failed_description'),
        })
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleVerifyCode = async (data: VerifyFormData) => {
    if (!leadId) return

    try {
      const result = await verifyCode.mutateAsync({ leadId, code: data.code })
      if (result.verified) {
        setPhoneNumber(fullPhoneNumber)
        setPhoneVerified(true)
        onNext()
      } else {
        verifyForm.setError('code', { message: t('resources.signup.phone.code_error') })
      }
    } catch {
      verifyForm.setError('code', { message: t('resources.signup.phone.code_error') })
    }
  }

  // Verification screen
  if (showVerification) {
    return (
      <div className="flex w-full flex-1 flex-col">
        <StepHeader
          title={t('resources.signup.phone.verify_title')}
          subtitle={t('resources.signup.phone.verify_subtitle', { phone: fullPhoneNumber })}
          onBack={() => setShowVerification(false)}
        />

        <form onSubmit={verifyForm.handleSubmit(handleVerifyCode)} className="flex flex-1 flex-col gap-4">
          <OnboardingInput
            {...verifyForm.register('code')}
            label={t('resources.signup.phone.code_label')}
            placeholder={t('resources.signup.phone.code_placeholder')}
            maxLength={6}
            autoComplete="one-time-code"
            className="text-center text-lg tracking-widest"
            error={verifyForm.formState.errors.code?.message}
          />

          <div className="mt-auto flex flex-col gap-2 pt-4 lg:mt-0 lg:pt-0">
            <OnboardingButton
              type="submit"
              isLoading={verifyCode.isPending}
              iconTrailing={ArrowRightIcon}
            >
              {t('resources.signup.common.verify')}
            </OnboardingButton>

            <OnboardingSkipButton
              onClick={handleSendCode}
              disabled={isSending}
            >
              {isSending ? t('resources.signup.common.sending') : t('resources.signup.common.resend_code')}
            </OnboardingSkipButton>
          </div>
        </form>
      </div>
    )
  }

  // Main phone number entry screen
  return (
    <div className="flex w-full flex-1 flex-col">
      <StepHeader
        title={t('resources.signup.phone.title')}
        subtitle={t('resources.signup.phone.subtitle')}
        onBack={onBack}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSendCode()
        }}
        className="flex flex-1 flex-col gap-4"
      >
        <div>
          <OnboardingLabel htmlFor="phoneNumber">{t('resources.signup.phone.label')}</OnboardingLabel>

          {/* Combined phone input with country selector */}
          <div
            className={cn(
              'flex items-center rounded-lg border bg-white shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]',
              phoneError || existingPhoneError
                ? 'border-[#f04438] focus-within:border-[#f04438] focus-within:ring-1 focus-within:ring-[#f04438]'
                : 'border-[#d5d7da] focus-within:border-[#181d27] focus-within:ring-1 focus-within:ring-[#181d27]'
            )}
          >
            {/* Country selector from react-international-phone */}
            <CountrySelector
              selectedCountry={country.iso2}
              onSelect={(iso2) => setCountry(iso2.iso2)}
              preferredCountries={['nl', 'be', 'de', 'fr', 'es', 'it', 'gb', 'us', 'pt', 'at']}
              renderButtonWrapper={({ children, rootProps }) => (
                <button
                  type="button"
                  {...rootProps}
                  className="flex items-center gap-1.5 rounded-l-lg px-3 py-2.5 text-base text-[#535862] cursor-pointer hover:bg-[#fafafa] transition-colors"
                >
                  {children}
                </button>
              )}
              dropdownStyleProps={{
                className: 'z-50 !rounded-xl !border !border-[rgba(0,0,0,0.08)] !shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03)] !max-h-[280px] !w-[300px]',
                listItemClassName: '!py-2.5 !px-3 hover:!bg-[#f9fafb] !text-sm !text-[#181d27]',
                listItemFlagClassName: '!mr-3',
                listItemCountryNameClassName: '!text-sm !font-medium',
                listItemDialCodeClassName: '!text-sm !text-[#535862]',
              }}
            />

            {/* Separator */}
            <div className="h-5 w-px flex-shrink-0 bg-[#e9eaeb]" />

            {/* Phone number input */}
            <input
              ref={inputRef}
              value={inputValue}
              onChange={handlePhoneValueChange}
              type="tel"
              id="phoneNumber"
              placeholder={t('resources.signup.phone.placeholder')}
              autoComplete="tel"
              className="flex-1 border-0 bg-transparent px-3 py-2.5 text-base text-[#181d27] placeholder:text-[#717680] focus:outline-none"
            />
          </div>

          {phoneError && (
            <p className="mt-1.5 text-sm text-[#f04438]">{phoneError}</p>
          )}

          {existingPhoneError && (
            <OnboardingAlert
              variant="error"
              title={t('resources.signup.phone.error_exists_title')}
              className="mt-3"
            >
              {t('resources.signup.phone.error_exists_before')}{' '}
              <a href="/login" className="cursor-pointer font-medium underline hover:no-underline">
                {t('resources.signup.phone.error_exists_link')}
              </a>{' '}
              {t('resources.signup.phone.error_exists_after')}
            </OnboardingAlert>
          )}
        </div>

        <div className="mt-auto pt-4 lg:mt-0 lg:pt-0">
          <OnboardingButton
            type="submit"
            isLoading={isSending}
            iconTrailing={ArrowRightIcon}
          >
            {t('resources.signup.common.continue')}
          </OnboardingButton>
        </div>
      </form>
    </div>
  )
}
